<template>
  <div class="text-left nutritionniste-page">
    <BreadcrumbsNav />

    <!-- Hero -->
    <section class="hero-section">
      <v-row align="center">
        <v-col cols="12" md="8">
          <div class="hero-badge">Diététiciens · Cuisines centrales · Cuisines concédées ou directes</div>
          <h1 class="hero-title">
            Des données terrain pour
            <br />
            <span class="hero-title--accent">des menus qui font vraiment sens.</span>
          </h1>
          <p class="hero-desc">
            Recevez les retours d'appréciation en temps réel depuis chaque cantine de votre réseau, affinez la
            composition nutritionnelle de vos repas et pilotez vos objectifs santé avec des données actionnables — pas
            des intuitions.
          </p>
        </v-col>
        <v-col cols="12" md="4" class="d-none d-md-flex justify-center">
          <v-img src="/static/images/doodles-dsfr/secondary/SitReadingDoodle.png" contain max-width="240" />
        </v-col>
      </v-row>
    </section>

    <!-- Rôles servis -->
    <section class="section-roles">
      <h2 class="section-title">Un outil pensé pour chaque acteur de la chaîne nutritionnelle</h2>
      <v-row class="mt-6">
        <v-col cols="12" md="4" v-for="role in roles" :key="role.title">
          <div class="role-card">
            <div class="role-card__icon">{{ role.icon }}</div>
            <h3 class="role-card__title">{{ role.title }}</h3>
            <ul class="role-card__list">
              <li v-for="item in role.items" :key="item">{{ item }}</li>
            </ul>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Feedback en temps réel -->
    <section class="section-feature">
      <v-row align="center">
        <v-col cols="12" md="6">
          <div class="feature-label">Feedback temps réel</div>
          <h2 class="section-title">Vos convives vous parlent — enfin vous pouvez les entendre</h2>
          <p class="feature-desc">
            Plus besoin d'attendre le bilan trimestriel avec la diététicienne. Chaque service génère automatiquement des
            données structurées sur l'appréciation des plats, les refus et les quantités consommées.
          </p>
          <div class="feature-stats">
            <div class="feature-stat" v-for="stat in feedbackStats" :key="stat.label">
              <span class="feature-stat__num">{{ stat.num }}</span>
              <span class="feature-stat__label">{{ stat.label }}</span>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="feedback-mockup">
            <div class="mockup-title">🍽️ Résultats — Votes enfants</div>
            <div class="mockup-sub">5 vote(s) enregistré(s) ce service</div>
            <div class="mockup-section">As-tu aimé le repas ?</div>
            <div class="mockup-bar" v-for="vote in votesExample" :key="vote.label">
              <span class="mockup-bar__label">{{ vote.emoji }} {{ vote.label }}</span>
              <div class="mockup-bar__track">
                <div class="mockup-bar__fill" :style="{ width: vote.pct, background: vote.color }"></div>
              </div>
              <span class="mockup-bar__val">{{ vote.val }}</span>
            </div>
            <div class="mockup-section mt-3">Plats qui ont posé problème</div>
            <div class="mockup-bar" v-for="plat in platsExample" :key="plat.label">
              <span class="mockup-bar__label">{{ plat.label }}</span>
              <div class="mockup-bar__track">
                <div class="mockup-bar__fill" :style="{ width: plat.pct, background: '#f59e0b' }"></div>
              </div>
              <span class="mockup-bar__val">{{ plat.val }}</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Capacités avancées -->
    <section class="section-advanced">
      <h2 class="section-title">Des fonctionnalités au service de votre expertise</h2>
      <v-row class="mt-6">
        <v-col cols="12" sm="6" v-for="feat in advancedFeatures" :key="feat.title">
          <div class="advanced-card">
            <div class="advanced-card__header">
              <span class="advanced-card__icon">{{ feat.icon }}</span>
              <div>
                <h3 class="advanced-card__title">{{ feat.title }}</h3>
                <span class="advanced-card__status" :class="'status--' + feat.status">{{ feat.statusLabel }}</span>
              </div>
            </div>
            <p class="advanced-card__desc">{{ feat.desc }}</p>
            <div class="advanced-card__benefit">→ {{ feat.benefit }}</div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Écosystème connecté -->
    <section class="section-ecosystem">
      <h2 class="section-title">Un écosystème qui connecte tous les acteurs</h2>
      <p class="section-subtitle">Labricot s'interface à vos applications existantes et se pilote selon vos règles.</p>
      <v-row class="mt-8" justify="center">
        <v-col cols="12" sm="6" md="3" v-for="actor in ecosystem" :key="actor.label">
          <div class="eco-card">
            <div class="eco-card__icon">{{ actor.icon }}</div>
            <div class="eco-card__label">{{ actor.label }}</div>
            <div class="eco-card__desc">{{ actor.desc }}</div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- CTA -->
    <section class="section-cta">
      <h2 class="cta-title">Prêt à piloter la nutrition de votre réseau ?</h2>
      <p class="cta-desc">
        Rejoignez les diététiciens et cuisines centrales, concédées ou directes qui font confiance à Labricot.
      </p>
      <div class="cta-actions">
        <v-btn color="primary" large class="cta-btn mr-4" href="mailto:contact@labsoft.fr">
          Demander une démo
        </v-btn>
        <v-btn outlined large class="cta-btn" :to="{ name: 'GestionnairePage2' }">
          ← Espace Gestionnaire
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script>
import BreadcrumbsNav from "@/components/BreadcrumbsNav"

export default {
  name: "NutritionistePage",
  components: { BreadcrumbsNav },
  data() {
    return {
      roles: [
        {
          icon: "🥗",
          title: "Diététiciens",
          items: [
            "Réception des retours d'appréciation des aliments en temps réel",
            "Consultation des statistiques par plat pour ajuster les nutriments",
            "Détection prédictive des troubles alimentaires (en développement)",
            "Travail collaboratif sur des menus conformes EGAlim et plus appétents",
          ],
        },
        {
          icon: "👨‍🍳",
          title: "Cuisiniers & Cuisines centrales",
          items: [
            "Réception des retours d'appréciation des recettes service par service",
            "Statistiques réseau multi-établissements pour adapter les préparations",
            "Conception de nouvelles recettes sur la base de données réelles de consommation",
            "Réduction du gaspillage par ajustement des grammages et garnitures",
          ],
        },
        {
          icon: "🎓",
          title: "Éducateurs & Apporteurs d'affaire",
          items: [
            "Création d'actions pédagogiques de recommandations Nutrition/Santé",
            "Animation de challenges anti-gaspillage avec les animateurs",
            "Suivi d'un plan d'actions et mesure des résultats",
            "Contenus pédagogiques au format Moodle pour les collégiens/lycéens",
          ],
        },
      ],
      feedbackStats: [
        { num: "Plat par plat", label: "Appréciation détaillée automatisée" },
        { num: "4 plats", label: "Notés individuellement chaque service" },
        { num: "Temps réel", label: "Transmission à la cuisine centrale" },
      ],
      votesExample: [
        { emoji: "😊", label: "Oui", pct: "20%", val: "1 (20%)", color: "#22c55e" },
        { emoji: "😐", label: "Moyen", pct: "20%", val: "1 (20%)", color: "#f59e0b" },
        { emoji: "😞", label: "Non", pct: "60%", val: "3 (60%)", color: "#ef4444" },
      ],
      platsExample: [
        { label: "🥗 L'entrée", pct: "25%", val: "1" },
        { label: "🍖 Le plat chaud", pct: "75%", val: "3" },
        { label: "🥦 L'accomp.", pct: "100%", val: "4" },
        { label: "🍮 Le dessert", pct: "0%", val: "0" },
      ],
      advancedFeatures: [
        {
          icon: "🔮",
          title: "Prédiction des troubles alimentaires",
          status: "dev",
          statusLabel: "En développement",
          desc:
            "Analyse prédictive sur les enquêtes pour identifier les risques d'anorexie, boulimie, hyperphagie ou orthorexie par patient, et prévenir les familles des convives concernés.",
          benefit: "Menus adaptés et prévention renforcée pour les convives vulnérables.",
        },
        {
          icon: "📊",
          title: "Statistiques réseau multi-cantines",
          status: "live",
          statusLabel: "Disponible",
          desc:
            "Consultez les statistiques d'appréciation à l'échelle de votre réseau d'établissements. Identifiez les recettes qui fonctionnent partout et celles à reformuler.",
          benefit: "Décisions basées sur des données agrégées, pas des remontées informelles.",
        },
        {
          icon: "🎓",
          title: "Module pédagogique Moodle",
          status: "deploy",
          statusLabel: "En déploiement",
          desc:
            "Contenu pédagogique, tests et défis d'acquisition des compétences conçus par cuisiniers, diététiciens et éducateurs. Format gamifié pour collégiens et lycéens.",
          benefit: "Engagement des convives dans la démarche bien-manger dès le plus jeune âge.",
        },
        {
          icon: "🧾",
          title: "Conformité EGAlim & macantine.gouv.fr",
          status: "live",
          statusLabel: "Disponible",
          desc:
            "Tableaux de bord qualitatifs, quantitatifs et nutritionnels. Aide à l'alimentation de macantine.gouv.fr dans l'esprit du pacte national anti-gaspillage.",
          benefit: "La loi EGAlim devient un cadre structurant, pas une source d'angoisse.",
        },
      ],
      ecosystem: [
        { icon: "🏫", label: "Écoles & cantines", desc: "Remontées quotidiennes du terrain" },
        { icon: "🍳", label: "Cuisines centrales", desc: "Feedback & ajustement des menus" },
        { icon: "🥗", label: "Diététiciens", desc: "Suivi nutritionnel et prédictif" },
        { icon: "🤝", label: "Associations", desc: "Circuits de don des excédents" },
      ],
    }
  },
}
</script>

<style scoped>
.nutritionniste-page {
  font-family: "Marianne", "Segoe UI", sans-serif;
}

/* Hero */
.hero-section {
  background: linear-gradient(135deg, #f3fbf6 0%, #edf7f1 60%, #d4f0e2 100%);
  border-radius: 20px;
  padding: 48px 40px;
  margin-bottom: 56px;
  border: 1px solid #a8d9bc;
}

.hero-badge {
  display: inline-block;
  background: #d4f0e2;
  color: #1d7a47;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 20px;
}

.hero-title {
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  font-weight: 900;
  line-height: 1.25;
  color: #1a1a2e;
  margin-bottom: 16px;
}

.hero-title--accent {
  color: #2d9e5f;
}

.hero-desc {
  font-size: 1rem;
  line-height: 1.7;
  color: #444;
  max-width: 560px;
}

/* Roles */
.section-roles {
  margin-bottom: 64px;
}

.role-card {
  background: white;
  border: 1px solid #d4f0e2;
  border-top: 4px solid #2d9e5f;
  border-radius: 14px;
  padding: 24px;
  height: 100%;
}

.role-card__icon {
  font-size: 2rem;
  margin-bottom: 12px;
}

.role-card__title {
  font-weight: 800;
  font-size: 1rem;
  color: #1a1a2e;
  margin-bottom: 14px;
}

.role-card__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.role-card__list li {
  font-size: 0.84rem;
  color: #555;
  line-height: 1.5;
  padding: 6px 0;
  border-bottom: 1px dashed #e8f5ee;
}

.role-card__list li:last-child {
  border-bottom: none;
}

.role-card__list li::before {
  content: "→ ";
  color: #2d9e5f;
  font-weight: 700;
}

/* Feature section */
.section-feature {
  margin-bottom: 64px;
  padding: 40px;
  background: #fafafa;
  border-radius: 20px;
}

.feature-label {
  display: inline-block;
  background: #2d9e5f;
  color: white;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
}

.feature-desc {
  font-size: 0.95rem;
  color: #555;
  line-height: 1.7;
  margin-bottom: 24px;
}

.feature-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.feature-stat {
  background: white;
  border: 1px solid #d4f0e2;
  border-radius: 10px;
  padding: 10px 16px;
}

.feature-stat__num {
  display: block;
  font-weight: 800;
  font-size: 0.95rem;
  color: #2d9e5f;
}

.feature-stat__label {
  font-size: 0.72rem;
  color: #888;
}

/* Feedback mockup */
.feedback-mockup {
  background: #1a1a2e;
  color: white;
  border-radius: 16px;
  padding: 24px;
  font-size: 0.84rem;
}

.mockup-title {
  font-weight: 800;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.mockup-sub {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  margin-bottom: 16px;
}

.mockup-section {
  font-weight: 700;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
}

.mt-3 {
  margin-top: 12px;
}

.mockup-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.mockup-bar__label {
  width: 100px;
  font-size: 0.78rem;
  flex-shrink: 0;
}

.mockup-bar__track {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.mockup-bar__fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.mockup-bar__val {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  width: 36px;
  text-align: right;
}

/* Advanced features */
.section-advanced {
  margin-bottom: 64px;
}

.advanced-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 24px;
  height: 100%;
  transition: box-shadow 0.2s ease;
}

.advanced-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.advanced-card__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 14px;
}

.advanced-card__icon {
  font-size: 1.8rem;
  flex-shrink: 0;
}

.advanced-card__title {
  font-weight: 800;
  font-size: 0.95rem;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.advanced-card__status {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.status--live {
  background: #d4f0e2;
  color: #1d7a47;
}

.status--deploy {
  background: #fde8da;
  color: #c94b1a;
}

.status--dev {
  background: #e4dfff;
  color: #5539cc;
}

.advanced-card__desc {
  font-size: 0.84rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 12px;
}

.advanced-card__benefit {
  font-size: 0.8rem;
  color: #2d9e5f;
  font-weight: 600;
}

/* Ecosystem */
.section-ecosystem {
  margin-bottom: 64px;
  text-align: center;
}

.eco-card {
  background: linear-gradient(145deg, #f3fbf6, #edf7f1);
  border: 1px solid #a8d9bc;
  border-radius: 14px;
  padding: 24px 16px;
  height: 100%;
}

.eco-card__icon {
  font-size: 2rem;
  margin-bottom: 10px;
}

.eco-card__label {
  font-weight: 800;
  font-size: 0.9rem;
  color: #1a1a2e;
  margin-bottom: 6px;
}

.eco-card__desc {
  font-size: 0.8rem;
  color: #666;
}

/* Section titles */
.section-title {
  font-size: clamp(1.3rem, 3vw, 1.7rem);
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 0;
}

/* CTA */
.section-cta {
  background: linear-gradient(135deg, #1a1a2e 0%, #1d3d2a 100%);
  border-radius: 20px;
  padding: 56px 40px;
  text-align: center;
  margin-bottom: 40px;
}

.cta-title {
  font-size: clamp(1.4rem, 3vw, 1.9rem);
  font-weight: 900;
  color: white;
  margin-bottom: 12px;
}

.cta-desc {
  color: rgba(255, 255, 255, 0.75);
  font-size: 1rem;
  margin-bottom: 28px;
}

.cta-btn {
  text-transform: none;
  font-weight: 700;
  border-radius: 10px;
}
</style>
